import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkspaceTeam = Awaited<ReturnType<typeof trpc.access.team.query>>;
export type ResourceScope = { id: string; type: "space" | "folder" | "board" };

export function useWorkspaceTeam() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const teamQuery = useQuery(trpc.access.team.queryOptions());
  const meQuery = useQuery(trpc.auth.me.queryOptions());
  const archiveMutation = useMutation(trpc.access.archiveTeam.mutationOptions());
  const createMutation = useMutation(trpc.access.createTeam.mutationOptions());
  const serviceAccountMutation = useMutation(trpc.access.createServiceAccount.mutationOptions());
  const inviteMutation = useMutation(trpc.access.invite.mutationOptions());
  const removeMutation = useMutation(trpc.access.removeMember.mutationOptions());
  const revokeMutation = useMutation(trpc.access.revokeInvitation.mutationOptions());
  const roleMutation = useMutation(trpc.access.updateMemberRole.mutationOptions());
  const updateMutation = useMutation(trpc.access.updateTeam.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      await Promise.all([teamQuery.refetch(), meQuery.refetch()]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load workspace team.");
    }
  }, [meQuery.refetch, teamQuery.refetch]);

  return {
    currentUserId: meQuery.data?.id ?? null,
    data: teamQuery.data ?? null,
    error: error ?? (teamQuery.error instanceof Error ? teamQuery.error.message : teamQuery.error ? String(teamQuery.error) : meQuery.error instanceof Error ? meQuery.error.message : meQuery.error ? String(meQuery.error) : null),
    isLoading: teamQuery.isPending || meQuery.isPending,
    archiveTeam: async (teamId: string) => {
      queryClient.setQueryData(trpc.access.team.queryKey(), await archiveMutation.mutateAsync({ teamId }));
    },
    createTeam: async (input: { memberIds: string[]; name: string }) => {
      queryClient.setQueryData(trpc.access.team.queryKey(), await createMutation.mutateAsync(input));
    },
    createServiceAccount: async (input: { name: string; role: "guest" | "member" }) => {
      const created = await serviceAccountMutation.mutateAsync(input);
      queryClient.setQueryData(trpc.access.team.queryKey(), created.team);
      return created.member;
    },
    invite: async (input: { displayName?: string; email: string; role: "admin" | "member" | "guest" }) => {
      const result = await inviteMutation.mutateAsync(input);
      await reload();
      return result;
    },
    reload,
    removeMember: async (userId: string) => {
      queryClient.setQueryData(trpc.access.team.queryKey(), await removeMutation.mutateAsync({ userId }));
    },
    revokeInvitation: async (invitationId: string) => {
      queryClient.setQueryData(trpc.access.team.queryKey(), await revokeMutation.mutateAsync({ invitationId }));
    },
    updateMemberRole: async (userId: string, role: "owner" | "admin" | "member" | "guest") => {
      queryClient.setQueryData(trpc.access.team.queryKey(), await roleMutation.mutateAsync({ userId, role }));
    },
    updateTeam: async (input: { memberIds: string[]; name: string; teamId: string }) => {
      queryClient.setQueryData(trpc.access.team.queryKey(), await updateMutation.mutateAsync(input));
    },
  };
}

export type ResourceAccess = Awaited<ReturnType<typeof trpc.access.scope.query>>;
export type ResourceAccessExplanation = Awaited<ReturnType<typeof trpc.access.explainScope.query>>;
